import React from 'react'
import {
  ActivityHistoryRowType,
  CPDManualFormValuesWithId
} from 'front-end/src/pages/activity-history/activityHistoryTypes'
import { Menu, MenuButton, MenuItem, MenuList } from 'front-end/src/components'
import IconDotsVertical from 'front-end/src/components/common/icon/IconDotsVertical'
import { Trans } from '@lingui/macro'

import {
  checkIsCPDEvidence,
  checkIsTopic
} from 'front-end/src/pages/activity-history/activityHistoryUtils'
import IconPencil from 'front-end/src/components/common/icon/IconPencil'
import IconTrash from 'front-end/src/components/common/icon/IconTrash'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import IconBookOpen from 'front-end/src/components/common/icon/IconBookOpen'
import IconBookCrossed from 'front-end/src/components/common/icon/IconBookCrossed'
import dayjs from 'shared/dayjs'
import { MarkItemsAsCpdMutationFn } from 'front-end/src/pages/activity-history/ActivityHistory.codegen'
import { CpdUserEntryItemType } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useCPDFeatureFlag } from 'front-end/src/app/hooks/useFeatureFlags'

type ActivityHistoryRowMenuProps = ActivityHistoryRowType & {
  openEditCPDModal: (formData: CPDManualFormValuesWithId) => void
  opedDeleteCPDModal: (id: number) => void
  markItemsAsCpd: MarkItemsAsCpdMutationFn
  isLoading: boolean
}

const ActivityHistoryRowMenu = ({
  openEditCPDModal,
  opedDeleteCPDModal,
  isLoading,
  markItemsAsCpd,
  ...row
}: ActivityHistoryRowMenuProps) => {
  const { theme } = useLooopTheme()
  const { cpdAlias } = useCPDFeatureFlag()

  const handleMarkItemsAsCpd = (isCpd: boolean) => async () => {
    if (isLoading) return

    await markItemsAsCpd({
      variables: {
        cpdInput: [
          {
            itemId: row.id,
            itemType: checkIsTopic(row)
              ? CpdUserEntryItemType.userTopic
              : CpdUserEntryItemType.userEvent,
            isCpd
          }
        ]
      }
    })
  }

  return (
    <Menu>
      <MenuButton
        circular
        boxSize="32px"
        bg="transparent"
        border={0}
        disabled={isLoading}
        className="ActivityHistory__menu__button"
        opacity="0"
        _focus={{
          opacity: 1
        }}
      >
        <IconDotsVertical boxSize="14px" />
      </MenuButton>
      <MenuList>
        {checkIsCPDEvidence(row) && (
          <>
            <MenuItem
              icon={<IconPencil />}
              onClick={() => {
                const duration = dayjs.duration(row.numberOfSeconds, 'seconds')
                const hours = Math.floor(duration.as('h'))
                // Remaining minutes = we have to remove hours
                const minutes = Math.floor(duration.as('m')) - hours * 60

                openEditCPDModal({
                  activityName: row.name,
                  durationHours: hours,
                  durationMinutes: minutes,
                  source: row?.source || '',
                  dateStarted: row.date_started as string,
                  dateCompleted: row.date_completed as string,
                  description: row.description,
                  id: row.id,
                  attachment: null,
                  fileName: row.fileName as string
                })
              }}
            >
              <Trans>Edit activity</Trans>
            </MenuItem>
            <MenuItem
              icon={<IconTrash />}
              color={theme.colors.red[500]}
              onClick={() => opedDeleteCPDModal(row.id)}
            >
              <Trans>Delete activity</Trans>
            </MenuItem>
          </>
        )}
        {!checkIsCPDEvidence(row) && (
          <>
            {!row.isCpdEntry && (
              <MenuItem
                icon={<IconBookOpen fontSize="18px" />}
                color={theme.colors.violet[600]}
                whiteSpace="nowrap"
                maxW="none"
                onClick={handleMarkItemsAsCpd(true)}
              >
                <Trans>Mark as {cpdAlias}</Trans>
              </MenuItem>
            )}
            {row.isCpdEntry && (
              <MenuItem
                icon={<IconBookCrossed />}
                whiteSpace="nowrap"
                maxW="none"
                onClick={handleMarkItemsAsCpd(false)}
              >
                <Trans>Unmark as {cpdAlias}</Trans>
              </MenuItem>
            )}
          </>
        )}
      </MenuList>
    </Menu>
  )
}

export default React.memo(ActivityHistoryRowMenu)
